// E2c'nin saf mantığı (ui.md §11, ADR-0016). Doğrulama yükleme yolununkiyle aynıdır (`validateRows`); ayrı doğrulayıcı yok.
import type { FieldValue } from "../../shared/api";
import { parseNumber } from "../../shared/normalize";
import { PLAIN, SOURCE_TYPES, validateRows, type Fields, type SourceType } from "../../shared/rows";
import { fieldsOf } from "../../shared/sources";

type Conditional = { field: string; when: (f: Fields) => boolean };
const conditionals = (t: SourceType): Conditional[] => (SOURCE_TYPES[t] as { conditional?: Conditional[] }).conditional ?? [];

/** Formdaki alanlar: kimlik (eşlenebilir olanlar; `lineSeq` sistemin), sonra zorunlu, sonra isteğe bağlı (`SOURCE_TYPES` sırasıyla). */
export function formFields(t: SourceType): { key: string[]; rest: string[] } {
  const spec = SOURCE_TYPES[t], names = fieldsOf(t);
  const key = (spec.key as readonly string[]).filter((f) => names.includes(f));
  const other = (a: readonly string[]) => a.filter((f) => !key.includes(f));
  return { key, rest: [...other(spec.required), ...other(spec.optional)] };
}

export type Cells = Record<string, FieldValue>;

/** Zorunlu mu: tanımda zorunlu ya da koşulu şu değerlerle sağlanıyor. */
export function isRequired(t: SourceType, field: string, cells: Cells): boolean {
  if ((SOURCE_TYPES[t].required as readonly string[]).includes(field)) return true;
  return conditionals(t).some((c) => c.field === field && c.when(cells as Fields));
}

/** Form metni → gövde değeri. Boş = null; sayı `tr` biçimiyle okunur (okunamazsa ham metin kalır, doğrulama hatayı yazar). */
export function toValue(t: SourceType, field: string, text: string): FieldValue {
  const v = text.trim();
  if (!v) return null;
  if ((SOURCE_TYPES[t].kinds as Record<string, string>)[field] === "number") { const r = parseNumber(v, "tr"); return r.ok ? r.value : v; }
  return v;
}

/** Alan → neden kodu. Yükleme yolunun `validateRows`'u, tek satır, sabit biçim; sunucuyla aynı hatalar. */
export function checkFields(t: SourceType, cells: Cells): Record<string, string> {
  const names = fieldsOf(t);
  // `validateRows` skips an all-empty row (an Excel blank line); in a form that is a record with every required field empty.
  if (names.every((n) => cells[n] == null)) return Object.fromEntries((SOURCE_TYPES[t].required as readonly string[]).map((f) => [f, "REQUIRED_EMPTY"]));
  const v = validateRows(t, [{ rowNo: 1, cells: Object.fromEntries(names.map((n) => [n, cells[n] ?? null])) }], Object.fromEntries(names.map((n) => [n, n])), PLAIN);
  const out: Record<string, string> = {};
  for (const e of v.rowErrors) out[e.column] ??= e.reason;
  for (const u of v.unknownValues) out[u.field] ??= "UNKNOWN_VALUE";
  return out;
}
